import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  ScrollView,
  StyleSheet,
  View,
  NativeModules,
} from "react-native";
import { launchCamera, launchImageLibrary } from "@utils/imagePickerCompat";
import { useSelector } from "react-redux";
import Header from "@components/HeaderWithBackAndTitle";
import checkPermissions from "@screens/SupportTree/components/Form/Images/checkPermissions";
import t from "@constants/localization";
import FetchCMSEntity from "@components/CMS/FetchCMSEntity";
import useCMSEvents from "@hooks/useCMSEvents";
import Button from "../../components/Button";
import {
  deletePrivateImage,
  getLeaderKycInfo,
  submitLeaderKyc,
  uploadPrivateImage,
} from "../../services/api";
import {
  getJsonData,
  removeJson,
  saveJsonData,
} from "../../utils/commonFunctions";
import AadharSection from "./AadharSection";
import AddressSection from "./AddressSection";
import KYCComment from "./Comment";
import PANSection from "./PanSection";
import ProcessingModal from "./ProcessingModal";
import SuccessModal from "./SuccessModal";
import KYCStatus from "./Status";
import UserPhotoSection from "./UserPhotoSection";
import analytics from "../../services/analytics";
import { CL_KYC } from "../../constants/analyticsEventsCT";
import testIds from "../../constants/testIds";

const PATH = "screens/KYCSetup/index";

const PAN_NUMBER_REGEX = /[A-Z]{5}[0-9]{4}[A-Z]{1}/;
const AADHAR_NUMBER_REGEX = /[0-9]{12}/;
const KYC_CMS_KEY = "KYC_CMS_KEY";

const KYCSetup = (props) => {
  const { route, navigation } = props;

  const goBack = () => navigation.goBack();

  const [userPhoto, setUserPhoto] = useState({ source: "", filename: "" });
  const [disableUserPhotoSection, setDisableUserPhotoSection] = useState(false);
  const [verifyUserPhotoSection, setVerifyUserPhotoSection] = useState(false);
  const [userPhotoRejectionReason, setUserPhotoRejectionReason] =
    useState(null);

  const [aadharNumber, setAadharNumber] = useState("");
  const [aadharFront, setAadharFront] = useState({ source: "", filename: "" });
  const [aadharBack, setAadharBack] = useState({ source: "", filename: "" });
  const [disableAadharSection, setDisableAadharSection] = useState(false);
  const [verifyAadharSection, setVerifyAadharSection] = useState(false);
  const [aadharRejectionReason, setAadharRejectionReason] = useState(null);

  const [panNumber, setPanNumber] = useState("");
  const [panPhoto, setPanPhoto] = useState("");
  const [disablePanSection, setDisablePanSection] = useState(false);
  const [verifyPanSection, setVerifyPanSection] = useState(false);
  const [panRejectionReason, setPanRejectionReason] = useState(null);

  const [sameAddress, setSameAddress] = useState("yes");
  const [addressPhoto, setAddressPhoto] = useState({
    source: "",
    filename: "",
  });
  const [disableAddressSection, setDisableAddressSection] = useState(false);
  const [verifyAddressSection, setVerifyAddressSection] = useState(false);
  const [addressRejectionReason, setAddressRejectionReason] = useState(null);

  const [userKycData, setUserKycData] = useState([]);
  const [kycData, setKycData] = useState([]);
  const [kycStatus, setKycStatus] = useState("NONE");
  const [kycComment, setKycComment] = useState(null);
  const [refresh, toggleRefresh] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [isAadharPanLinked, setIsAadharPanLinked] = useState(true);

  const appSettings = useSelector((s) => s.configReducer.appSettings) || {};
  const { cmsPoweredEntities = {} } = appSettings || {};

  useEffect(() => {
    setShowProcessingModal(true);
    getLeaderKycInfo()
      .then(({ data }) => {
        setShowProcessingModal(false);
        const dataObj = data.reduce(
          (acc, curr) => ({ ...acc, [curr.kyc_item_name]: curr }),
          {},
        );

        for (const i in dataObj) {
          delete dataObj[i].cl_id;
          delete dataObj[i].verified_at;
          delete dataObj[i].created_at;
          delete dataObj[i].submitted_at;
          delete dataObj[i].verified_by;
          delete dataObj[i].rejected_by;
          delete dataObj[i].rejected_at;
        }

        setUserKycData(data);
        setKycData(dataObj);
        setFetched(true);
        analytics.trackEvent(CL_KYC.KYC_LANDING);
      })
      .catch((err) => {
        console.warn(err);
        setShowProcessingModal(false);
        navigation.navigate("Home");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  useEffect(() => {
    if (kycStatus === "NONE" && fetched) {
      const docs = {
        kyc_user_photo: setUserPhoto,
        kyc_aadhar_front: setAadharFront,
        kyc_aadhar_back: setAadharBack,
        kyc_pan_image: setPanPhoto,
        kyc_address_document: setAddressPhoto,
      };

      const numbers = {
        kyc_aadhar_number: setAadharNumber,
        kyc_pan_number: setPanNumber,
      };

      Object.keys(docs).forEach((doc) => {
        getJsonData(doc).then((value) => {
          if (value) {
            docs[doc](value);
          }
        });
      });

      Object.keys(numbers).forEach((number) => {
        getJsonData(number).then((value) => {
          if (value) {
            numbers[number](value);
          }
        });
      });
    }
  }, [kycStatus, fetched]);

  useEffect(() => {
    if (kycData && typeof kycData === "object" && Object.keys(kycData).length) {
      setKycStatus(kycData?.user_photo?.kyc_status);
      setKycComment(kycData?.user_photo?.kyc_comment);
      setIsAadharPanLinked(
        kycData?.is_pan_linked_to_aadhar?.kyc_item_value === "true",
      );
    }
  }, [kycData]);

  const mapKycDataToState = useCallback(
    (kycItemName, setItem, image = false) => {
      const value = kycData[kycItemName]?.kyc_item_value;

      setItem((i) => (image ? { ...i, source: value } : value));
    },
    [kycData],
  );

  useEffect(() => {
    switch (kycStatus) {
      case "VERIFIED":
      case "PROCESSING": {
        mapKycDataToState("user_photo", setUserPhoto, true);
        setDisableUserPhotoSection(true);

        mapKycDataToState("aadhar_number", setAadharNumber);
        mapKycDataToState("aadhar_front", setAadharFront, true);
        mapKycDataToState("aadhar_back", setAadharBack, true);
        setDisableAadharSection(true);

        mapKycDataToState("pan_number", setPanNumber);
        mapKycDataToState("pan_image", setPanPhoto, true);
        setDisablePanSection(true);

        mapKycDataToState("is_address_same_as_aadhar", setSameAddress);
        mapKycDataToState("address_document", setAddressPhoto, true);
        setDisableAddressSection(true);
        break;
      }
      case "PARTIALLY_VERIFIED": {
        const rejectedDocuments = Object.keys(kycData).reduce((acc, curr) => {
          if (kycData[curr].kyc_item_status === "REJECTED") {
            return acc.concat(kycData[curr]?.kyc_item_name);
          }
          return acc;
        }, []);

        const verifiedDocuments = Object.keys(kycData).reduce((acc, curr) => {
          if (kycData[curr].kyc_item_status === "VERIFIED") {
            return acc.concat(kycData[curr]?.kyc_item_name);
          }
          return acc;
        }, []);

        if (!rejectedDocuments.includes("user_photo")) {
          mapKycDataToState("user_photo", setUserPhoto, true);
          setDisableUserPhotoSection(true);
          if (verifiedDocuments.includes("user_photo")) {
            setVerifyUserPhotoSection(true);
          }
        } else {
          setUserPhotoRejectionReason(
            kycData?.user_photo?.kyc_item_rejection_comment,
          );
        }

        if (
          !rejectedDocuments.includes("aadhar_number") &&
          !rejectedDocuments.includes("aadhar_front") &&
          !rejectedDocuments.includes("aadhar_back")
        ) {
          mapKycDataToState("aadhar_number", setAadharNumber);
          mapKycDataToState("aadhar_front", setAadharFront, true);
          mapKycDataToState("aadhar_back", setAadharBack, true);
          setDisableAadharSection(true);
          if (
            verifiedDocuments.includes("aadhar_number") &&
            verifiedDocuments.includes("aadhar_front") &&
            verifiedDocuments.includes("aadhar_back")
          ) {
            setVerifyAadharSection(true);
          }
        } else {
          setAadharRejectionReason(
            kycData?.aadhar_number?.kyc_item_rejection_comment,
          );
        }

        if (
          !rejectedDocuments.includes("pan_number") &&
          !rejectedDocuments.includes("pan_image")
        ) {
          mapKycDataToState("pan_number", setPanNumber);
          mapKycDataToState("pan_image", setPanPhoto, true);
          setDisablePanSection(true);
          if (
            verifiedDocuments.includes("pan_number") &&
            verifiedDocuments.includes("pan_image")
          ) {
            setVerifyPanSection(true);
          }
        } else {
          setPanRejectionReason(
            kycData?.pan_number?.kyc_item_rejection_comment,
          );
        }

        if (
          !rejectedDocuments.includes("is_address_same_as_aadhar") &&
          !rejectedDocuments.includes("address_document")
        ) {
          mapKycDataToState("is_address_same_as_aadhar", setSameAddress);
          mapKycDataToState("address_document", setAddressPhoto, true);
          setDisableAddressSection(true);
          if (
            (kycData?.is_address_same_as_aadhar?.kyc_item_value === "no" &&
              verifiedDocuments.includes("address_document")) ||
            (kycData?.is_address_same_as_aadhar?.kyc_item_value === "yes" &&
              verifiedDocuments.includes("is_address_same_as_aadhar"))
          ) {
            setVerifyAddressSection(true);
          }
        } else {
          setAddressRejectionReason(
            kycData?.is_address_same_as_aadhar?.kyc_item_rejection_comment,
          );
        }
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycStatus]);

  const uploadUserPhoto = () => {
    uploadPhoto(setUserPhoto, "kyc_user_photo");
  };

  const uploadAadharFront = () => {
    uploadPhoto(setAadharFront, "kyc_aadhar_front");
  };

  const uploadAadharBack = () => {
    uploadPhoto(setAadharBack, "kyc_aadhar_back");
  };

  const uploadPanPhoto = () => {
    uploadPhoto(setPanPhoto, "kyc_pan_image");
  };
  const uploadAddressProof = () => {
    uploadPhoto(setAddressPhoto, "kyc_address_document");
  };

  const removeUserPhoto = () => {
    removePhoto(userPhoto.filename, setUserPhoto, "kyc_user_photo");
  };

  const removeAadharFront = () => {
    removePhoto(aadharFront.filename, setAadharFront, "kyc_aadhar_front");
  };

  const removeAadharBack = () => {
    removePhoto(aadharBack.filename, setAadharBack, "kyc_aadhar_back");
  };

  const removePanPhoto = () => {
    removePhoto(panPhoto.filename, setPanPhoto, "kyc_pan_image");
  };

  const removeAddressProof = () => {
    removePhoto(addressPhoto.filename, setAddressPhoto, "kyc_address_document");
  };

  const onImageSelect = (localImgName, setPhotoState) => async (response) => {
    if (response.didCancel) {
      return;
    } else if (response.error) {
      console.warn(response.error);
      return;
    }
    const allowedFileFormat = ["jpg", "jpeg", "png"];
    if (!allowedFileFormat.includes(response.type?.split("/")[1])) {
      // eslint-disable-next-line no-alert
      alert(t(`${PATH}/unsupportedFileFormat`));
      return;
    }
    setShowProcessingModal(true);
    const data = new FormData();
    data.append("image", {
      uri: response.uri,
      type: response.type,
      name: response.fileName,
    });
    try {
      const response = await uploadPrivateImage(data);
      const uploadData = response?.data;
      if (uploadData?.url) {
        const remoteFilename = uploadData.url.split("/").reverse()[0];
        await saveJsonData(localImgName, {
          source: uploadData.url,
          filename: remoteFilename,
        });
        setPhotoState({
          source: uploadData.url,
          filename: remoteFilename,
        });
      }
    } catch (e) {
      console.log(e);
    } finally {
      setShowProcessingModal(false);
    }
  };

  const uploadPhoto = async (setPhotoState, localImgName) => {
    try {
      await checkPermissions();
    } catch (e) {
      const { AppSettingsIntentModule } = NativeModules;
      Alert.alert(
        t(`${PATH}/permissionsDenied`),
        t(`${PATH}/askForPermissionMsg`),
        [
          {
            text: t(`${PATH}/tryAgain`),
            onPress: () => uploadPhoto(setPhotoState, localImgName),
          },
          {
            text: t(`${PATH}/goToSettings`),
            onPress: () => AppSettingsIntentModule.openAppSettings(),
          },
        ],
      );

      console.warn(e);
      setShowProcessingModal(false);
      return;
    }
    try {
      Alert.alert(
        t(`${PATH}/uploadPhoto`),
        t(`${PATH}/youWantToPickPhotoFrom`),
        [
          {
            text: t(`${PATH}/fromCamera`),
            onPress: () => {
              launchCamera(
                { mediaType: "photo", maxHeight: 1024, maxWidth: 1024, quality: 0.5 },
                onImageSelect(localImgName, setPhotoState),
              );
            },
          },
          {
            text: t(`${PATH}/fromGallery`),
            onPress: () => {
              launchImageLibrary(
                { mediaType: "photo", maxHeight: 1024, maxWidth: 1024, quality: 0.5 },
                onImageSelect(localImgName, setPhotoState),
              );
            },
          },
        ],
        { cancelable: true },
      );
    } catch (e) {
      console.warn(e);
      setShowProcessingModal(false);
    }
  };
  const removePhoto = async (filename, setPhotoState, localImgName) => {
    if (!filename) {
      return;
    }
    await deletePrivateImage(filename);
    setPhotoState({ source: "", filename: "" });
    removeJson(localImgName);
  };

  const {
    address1 = "",
    address2 = "",
    pincode = "",
    city = "",
  } = useSelector((s) => s.auth.user) || {};
  const userAddress = useMemo(
    () =>
      `${address1 ? `${address1.trim()},` : ""}  ${
        address2 ? address2.trim() : ""
      }`.trim(),
    [address1, address2],
  );

  const navigateToTLAddress = useCallback(() => {
    navigation.navigate("TLAddress", {
      fromBottomBar: true,
      pincode,
      city,
      source: "kyc_flow",
    });
  }, [city, navigation, pincode]);

  const onSubmit = async () => {
    const kyc_items = [
      {
        kyc_item_name: "user_photo",
        kyc_item_value: userPhoto.source,
      },
      {
        kyc_item_name: "aadhar_number",
        kyc_item_value: aadharNumber,
      },
      {
        kyc_item_name: "aadhar_front",
        kyc_item_value: aadharFront.source,
      },
      {
        kyc_item_name: "aadhar_back",
        kyc_item_value: aadharBack.source,
      },
      {
        kyc_item_name: "pan_number",
        kyc_item_value: panNumber,
      },
      {
        kyc_item_name: "pan_image",
        kyc_item_value: panPhoto.source,
      },
      {
        kyc_item_name: "is_address_same_as_aadhar",
        kyc_item_value: sameAddress,
      },
    ];

    if (sameAddress === "no") {
      kyc_items.push({
        kyc_item_name: "address_document",
        kyc_item_value: addressPhoto.source,
      });
    }

    submitLeaderKyc({ kyc_items }).then((_) => {
      toggleRefresh((refreshToggler) => !refreshToggler);
      setShowSuccessModal(true);
      analytics.trackEvent(CL_KYC.KYC_SUBMIT);
    });
  };

  const trackEvent = useCMSEvents("kyclanding");

  const submitDisabled = useMemo(() => {
    if (!userAddress) return true;
    switch (kycStatus) {
      case "NONE":
      case "PARTIALLY_VERIFIED":
      case "REJECTED": {
        if (
          !userPhoto.source ||
          !AADHAR_NUMBER_REGEX.test(aadharNumber) ||
          !aadharFront.source ||
          !aadharBack.source ||
          !PAN_NUMBER_REGEX.test(panNumber) ||
          !panPhoto.source ||
          (sameAddress === "no" && !addressPhoto.source)
        ) {
          return true;
        }
        return false;
      }
      default: {
        return false;
      }
    }
  }, [
    userAddress,
    aadharBack.source,
    aadharFront.source,
    aadharNumber,
    addressPhoto.source,
    panNumber,
    panPhoto.source,
    sameAddress,
    userPhoto.source,
    kycStatus,
  ]);

  return (
    <View style={styles.body} testID={testIds.KYC_container}>
      <Header
        headertitle={t(`${PATH}/completeKYC`)}
        navigation={navigation}
        route={route}
        hideShadow={false}
      />
      <ScrollView
        testID={testIds.kyc_scroll}
        contentContainerStyle={{ paddingBottom: 50 }}
      >
        {cmsPoweredEntities &&
        typeof cmsPoweredEntities === "object" &&
        KYC_CMS_KEY in cmsPoweredEntities ? (
          <FetchCMSEntity
            trackEvent={trackEvent}
            id={cmsPoweredEntities[KYC_CMS_KEY]}
            type="PAGE"
            isPaddingVertical={false}
          />
        ) : null}
        <View style={styles.container}>
          <KYCStatus
            statusText={kycStatus}
            isAadharPanLinked={isAadharPanLinked}
          />
          <KYCComment kycComment={kycComment} />
          <UserPhotoSection
            {...{
              userPhoto,
              uploadUserPhoto,
              removeUserPhoto,
              disableUserPhotoSection,
              kycStatus,
            }}
            rejectionComment={userPhotoRejectionReason}
            verified={verifyUserPhotoSection}
          />
          <AadharSection
            {...{
              aadharBack,
              aadharFront,
              aadharNumber,
              setAadharNumber,
              uploadAadharFront,
              uploadAadharBack,
              removeAadharFront,
              removeAadharBack,
              disableAadharSection,
            }}
            testAadharID={testIds.enter_aadhar}
            rejectionComment={aadharRejectionReason}
            verified={verifyAadharSection}
          />
          <PANSection
            testPANid={testIds.enter_PAN}
            {...{
              panNumber,
              setPanNumber,
              panPhoto,
              uploadPanPhoto,
              removePanPhoto,
              disablePanSection,
            }}
            rejectionComment={panRejectionReason}
            verified={verifyPanSection}
          />
          <AddressSection
            userAddress={userAddress}
            navigateToTLAddress={navigateToTLAddress}
            sameAddress={sameAddress}
            setSameAddress={setSameAddress}
            {...{
              addressPhoto,
              uploadAddressProof,
              removeAddressProof,
              disableAddressSection,
            }}
            rejectionComment={addressRejectionReason}
            verified={verifyAddressSection}
          />
          {(kycStatus === "NONE" ||
            kycStatus === "REJECTED" ||
            (kycStatus === "PARTIALLY_VERIFIED" &&
              userKycData.find(
                (item) => item.kyc_item_status === "REJECTED",
              ))) && (
            <Button
              btnText={t(`${PATH}/submit`)}
              borderRadius={10}
              onPress={onSubmit}
              buttonStyle={{ width: "100%", marginTop: 30, marginBottom: 30 }}
              disallowClickWhileSubmitting={true}
              disabled={submitDisabled}
            />
          )}
        </View>
      </ScrollView>
      <ProcessingModal showProcessingModal={showProcessingModal} />
      <SuccessModal
        showSuccessModal={showSuccessModal}
        setShowSuccessModal={setShowProcessingModal}
        goBack={goBack}
      />
    </View>
  );
};

export default KYCSetup;

const styles = StyleSheet.create({
  body: {
    backgroundColor: "white",
    flex: 1,
  },
  container: {
    paddingHorizontal: 15,
  },
});
